<template>
  <section class="paper-section">
    <div class="paper-container">
      <!-- Section Header -->
      <header class="paper-header">
        <div class="row align-items-center">
          <div class="col">
            <h2 class="paper-header__title">
              <button
                type="button"
                class="btn back_btn back-btn"
                aria-label="Go back"
                @click="$router.go(-1)"
              >
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
              </button>
              Add Paper
            </h2>
          </div>
        </div>
      </header>

      <!-- Panel Content -->
      <div class="paper-card">
        <div class="paper-card__wrapper">
          <div class="paper-card__modal">
            <!-- Tabs Navigation -->
            <nav class="paper-tabs" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                role="tab"
                class="paper-tabs__item"
                :class="{ 'paper-tabs__item--active': currentTab === tab.id }"
                :aria-selected="currentTab === tab.id"
                @click="currentTab = tab.id"
              >
                <i :class="['paper-tabs__icon', tab.icon]" aria-hidden="true"></i>
                <span class="paper-tabs__label">{{ tab.label }}</span>
              </button>
            </nav>

            <!-- Dynamic Tab Content -->
            <main class="paper-card__body">
              <keep-alive>
                <component :is="activeComponent"></component>
              </keep-alive>
            </main>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { bus } from '@/main';
import AddPaperFromLocal from './add-paper-from-local';
import AddPaperFromQuery from './add-paper-from-query';
import AddPaperFromDoi from './add-paper-from-doi';

export default {
  name: 'AddPaperfiles',
  components: {
    AddPaperFromLocal,
    AddPaperFromQuery,
    AddPaperFromDoi
  },
  data() {
    return {
      currentTab: 'local',
      tabs: [
        { id: 'local', label: 'Upload Paper', icon: 'fa fa-cloud-upload', component: 'AddPaperFromLocal' },
        { id: 'query', label: 'Find Papers', icon: 'fa fa-search', component: 'AddPaperFromQuery' },
        { id: 'doi', label: 'Paper DOI', icon: 'fa fa-link', component: 'AddPaperFromDoi' }
      ]
    };
  },
  computed: {
    activeComponent() {
      const active = this.tabs.find(tab => tab.id === this.currentTab);
      return active ? active.component : 'AddPaperFromLocal';
    }
  },
  methods: {
    closeCreatePaperpilePopup() {
      bus.$emit('addPaperPilePopupStateBus', false);
      document.body.classList.remove('body_right_scroll_remove');
    }
  }
};
</script>

<style lang="scss" scoped>
// Variables
$font-stack: 'MyriadProRegular', 'Lato', sans-serif;
$bg-dark: #111111;
$bg-card: rgba(9, 9, 9, 0.95);
$bg-input: #1a1a1a;
$border-color: #333333;
$border-light: #222222;
$text-primary: #ffffff;
$text-muted: #777777;
$accent-active: linear-gradient(to bottom, #87cefa, #8bb7ff);
$focus-gold: #ffd700;

.paper-section {
  font-family: $font-stack;
  height: 100%;

  a {
    text-decoration: none;
  }

  & > .row {
    min-height: calc(100dvh - 80px);
  }

  .paper-container {
    background: $bg-card;
    box-shadow: -3px 1px 10px rgba(138, 145, 153, 0.05);
    border-radius: 5px;
    padding: 15px;
    height: 100%;

    @media (max-width: 768px) {
      min-height: calc(100dvh - 90px);
    }
  }

  // Header Styles
  .paper-header {
    padding: 0 15px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    margin: 0 -15px;

    &__title {
      color: $text-primary;
      font-size: 1.5rem;
      text-transform: uppercase;
      font-weight: 700;
      margin: 0;
      display: flex;
      align-items: center;
    }
  }

  // Card & Modal Styles
  .paper-card {
    width: 100%;
    height: 100%;
    color: $text-primary;
    padding: 0 15px;

    &__wrapper {
      display: flex;
      padding-top: 30px;
    }

    &__modal {
      background: linear-gradient(
        -30deg,
        rgba(37, 44, 84, 0.6) 0%,
        rgba(0, 0, 0, 0.6) 50%,
        rgba(38, 38, 38, 0.6) 50%,
        rgba(44, 44, 84, 0.6) 100%
      );
      width: 100%;
      border-radius: 8px;
      overflow: hidden;
    }

    &__body {
      padding: 30px 20px;
    }
  }

  // Tabs Navigation
  .paper-tabs {
    display: flex;
    background: $bg-dark;
    border-bottom: 1px solid $border-color;

    &__item {
      flex: 1;
      padding: 16px 12px;
      text-align: center;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      color: $text-primary;
      background: transparent;
      border: none;
      border-right: 1px solid $border-light;
      font-family: inherit;
      transition: background 0.2s ease, color 0.2s ease;

      &:hover:not(&--active) {
        background: $bg-input;
      }

      &--active {
        background: $accent-active !important;
        color: #000000;
        border-bottom: 1px solid #000000;
      }

      &:last-child {
        border-right: none;
      }

      &:focus-visible {
        outline: 2px solid $focus-gold;
        outline-offset: -2px;
      }

      @media (max-width: 576px) {
        padding: 12px 8px;
      }
    }

    &__icon {
      font-size: 1.5rem;

      @media (max-width: 576px) {
        font-size: 1.25rem;
      }
    }

    &__label {
      font-size: 22px;
      font-weight: 500;
      text-transform: capitalize;

      @media (max-width: 576px) {
        font-size: 0.85rem;
      }
    }
  }
}
</style>